import {
  PrinterTicketPayload,
  RoutedPrintPayload,
  PrinterRoutingMode,
} from '../types/printerRouting';
import {
  splitTicketByCategory,
  stripAllMarkers,
  type TicketMetadata,
  type SplitTicketResult,
} from '../utils/ticketSplitter';
import { getRoutingConfig, getPrinterForCategory } from './printerRoutingService';
import { getPrinterProfiles } from './printerProfileService';
import { profileFor, effectiveProfileFor } from './multiPrinterProfile';
import type { PrinterProfile } from '../types/printerProfile';

/**
 * v5 multi-imprimantes : fonction fournie par l'appelant pour (re)construire le contenu
 * d'un ticket avec un PROFIL donné. Si elle est absente, on garde le contenu pré-construit
 * (options.fullTicketContent) → comportement historique inchangé (aucune régression).
 *   - forKitchenCategory: si défini, on veut le contenu CUISINE de cette catégorie (texte).
 */
export type RenderForProfile = (
  profile: PrinterProfile | null,
  opts?: { forKitchenCategory?: string }
) => string;

/** Trouve un profil TEXTE sûr (cuisine + repli compat). Préfère 'noanchor', puis 'unified'. */
const findTextProfile = async (): Promise<PrinterProfile | null> => {
  try {
    const all = await getPrinterProfiles();
    return (
      all.find((p) => p.type === 'noanchor') ||
      all.find((p) => p.type === 'unified') ||
      all.find((p) => p.type === 'modern') ||
      null
    );
  } catch {
    return null;
  }
};

export interface BuildPayloadOptions {
  fullTicketContent: string;
  orderNumber: string;
  orderDate: string;
  orderType: 'takeaway' | 'dine-in';
  tableNumber?: string;
  customerName?: string;
  paymentMethod?: 'cash' | 'card';
  /**
   * v5 multi-imprimantes (optionnel) : si fourni, chaque imprimante (re)construit son
   * contenu avec SON profil. Si absent → on utilise fullTicketContent partout (historique).
   */
  renderForProfile?: RenderForProfile;
}

const shouldPrintForPaymentFilter = (
  paymentFilter: string,
  paymentMethod?: 'cash' | 'card'
): boolean => {
  console.log('[shouldPrintForPaymentFilter] Checking:', {
    paymentFilter,
    paymentMethod,
    paymentMethodType: typeof paymentMethod,
  });

  if (paymentFilter === 'all') {
    console.log('[shouldPrintForPaymentFilter] ✓ Filter is "all", allowing print');
    return true;
  }

  if (!paymentMethod) {
    console.log('[shouldPrintForPaymentFilter] ✓ No payment method specified, allowing print');
    return true;
  }

  const result = paymentFilter === paymentMethod;
  console.log(`[shouldPrintForPaymentFilter] ${result ? '✓' : '✗'} Comparing "${paymentFilter}" === "${paymentMethod}" => ${result}`);
  return result;
};

export const buildClassicPayload = async (
  options: BuildPayloadOptions,
  mainPrinterId?: string
): Promise<RoutedPrintPayload> => {
  const config = await getRoutingConfig();
  const printerId = mainPrinterId || config.mainPrinterId || 'default';

  const tickets: PrinterTicketPayload[] = [];

  // Vérifier le filtre de paiement pour l'imprimante principale
  const shouldPrint = shouldPrintForPaymentFilter(
    config.mainPrinterPaymentFilter,
    options.paymentMethod
  );

  console.log('[buildClassicPayload] Payment filter check:', {
    paymentFilter: config.mainPrinterPaymentFilter,
    paymentMethod: options.paymentMethod,
    shouldPrint
  });

  if (shouldPrint) {
    // v5 multi-imprimantes : si renderForProfile est fourni, on construit le ticket CLIENT
    // avec le profil de l'imprimante principale (mainPrinterProfileId), sinon repli actif.
    let content: string;
    if (options.renderForProfile) {
      const textProfile = await findTextProfile();
      const wanted = await profileFor(config.mainPrinterProfileId);
      const profile = effectiveProfileFor(printerId, wanted, textProfile);
      content = stripAllMarkers(options.renderForProfile(profile));
    } else {
      content = stripAllMarkers(options.fullTicketContent);
    }

    tickets.push({
      printerId,
      printerName: 'Imprimante principale',
      content,
      isPrimary: true,
    });
  }

  return {
    orderNumber: options.orderNumber,
    orderDate: options.orderDate,
    orderType: options.orderType,
    tableNumber: options.tableNumber,
    customerName: options.customerName,
    paymentMethod: options.paymentMethod,
    mode: 'classic',
    tickets,
  };
};

export const buildByCategoryPayload = async (
  options: BuildPayloadOptions
): Promise<RoutedPrintPayload> => {
  const metadata: TicketMetadata = {
    orderNumber: options.orderNumber,
    orderDate: options.orderDate,
    orderType: options.orderType,
    tableNumber: options.tableNumber,
    customerName: options.customerName,
  };

  const config = await getRoutingConfig();
  const tickets: PrinterTicketPayload[] = [];

  // v5 multi-imprimantes : pour la CUISINE on veut TOUJOURS du texte (jamais raster).
  // Si renderForProfile est fourni, on re-rend un contenu TEXTE puis on le découpe par
  // catégorie. Sinon, on garde le contenu pré-construit (historique).
  const textProfile = options.renderForProfile ? await findTextProfile() : null;
  const sourceContent =
    options.renderForProfile
      ? options.renderForProfile(textProfile) // cuisine = texte, tous les articles + marqueurs [CAT:]
      : options.fullTicketContent;

  const splitResult: SplitTicketResult = splitTicketByCategory(sourceContent, metadata);

  for (const categoryTicket of splitResult.tickets) {
    const mapping = await getPrinterForCategory(categoryTicket.categoryId);

    if (mapping) {
      // Vérifier le filtre de paiement pour cette catégorie
      const shouldPrint = shouldPrintForPaymentFilter(
        mapping.paymentFilter,
        options.paymentMethod
      );

      console.log('[buildByCategoryPayload] Payment filter check:', {
        category: categoryTicket.categoryName,
        paymentFilter: mapping.paymentFilter,
        paymentMethod: options.paymentMethod,
        shouldPrint
      });

      if (shouldPrint) {
        tickets.push({
          printerId: mapping.printerId,
          printerName: mapping.printerName,
          categoryId: categoryTicket.categoryId,
          categoryName: categoryTicket.categoryName,
          content: categoryTicket.content,
          isPrimary: false,
        });
      }
    } else {
      console.warn(
        `No printer mapping found for category: ${categoryTicket.categoryName}`
      );
    }
  }

  return {
    orderNumber: options.orderNumber,
    orderDate: options.orderDate,
    orderType: options.orderType,
    tableNumber: options.tableNumber,
    customerName: options.customerName,
    paymentMethod: options.paymentMethod,
    mode: 'by-category',
    tickets,
  };
};

export const buildHybridPayload = async (
  options: BuildPayloadOptions
): Promise<RoutedPrintPayload> => {
  console.log('[buildHybridPayload] Starting hybrid payload construction');
  console.log('[buildHybridPayload] Order:', options.orderNumber);

  const config = await getRoutingConfig();
  console.log('[buildHybridPayload] Config:', {
    mode: config.mode,
    enabled: config.enabled,
    mainPrinterId: config.mainPrinterId,
    mappingsCount: config.categoryMappings.length,
    mappings: config.categoryMappings
  });

  if (!config.mainPrinterId) {
    console.warn('[buildHybridPayload] Aucune imprimante principale configurée, fallback vers mode classique');
    return buildClassicPayload(options, 'default');
  }

  const metadata: TicketMetadata = {
    orderNumber: options.orderNumber,
    orderDate: options.orderDate,
    orderType: options.orderType,
    tableNumber: options.tableNumber,
    customerName: options.customerName,
  };

  console.log('[buildHybridPayload] Calling splitTicketByCategory...');

  // v5 multi-imprimantes : CLIENT = profil principal (raster possible) ; CUISINE = texte.
  const textProfile = options.renderForProfile ? await findTextProfile() : null;
  // Contenu CLIENT (ticket complet) avec son profil
  let clientContent = options.fullTicketContent;
  if (options.renderForProfile) {
    const wanted = await profileFor(config.mainPrinterProfileId);
    const clientProfile = effectiveProfileFor(config.mainPrinterId, wanted, textProfile);
    clientContent = options.renderForProfile(clientProfile);
  }
  // Contenu CUISINE (texte) à découper par catégorie
  const kitchenSource = options.renderForProfile
    ? options.renderForProfile(textProfile)
    : options.fullTicketContent;

  const splitResult: SplitTicketResult = splitTicketByCategory(
    kitchenSource,
    metadata
  );

  console.log('[buildHybridPayload] Split result:', {
    categoriesFound: splitResult.tickets.length,
    categories: splitResult.tickets.map(t => ({ id: t.categoryId, name: t.categoryName }))
  });

  const tickets: PrinterTicketPayload[] = [];

  // Ticket principal: nettoyer les marqueurs de catégorie
  // Vérifier le filtre de paiement pour l'imprimante principale
  const shouldPrintMain = shouldPrintForPaymentFilter(
    config.mainPrinterPaymentFilter,
    options.paymentMethod
  );

  console.log('[buildHybridPayload] Main printer payment filter check:', {
    paymentFilter: config.mainPrinterPaymentFilter,
    paymentMethod: options.paymentMethod,
    shouldPrint: shouldPrintMain
  });

  if (shouldPrintMain) {
    const cleanMainTicket = stripAllMarkers(clientContent);
    tickets.push({
      printerId: config.mainPrinterId,
      printerName: 'Imprimante principale',
      content: cleanMainTicket,
      isPrimary: true,
    });
    console.log('[buildHybridPayload] ✓ Ticket principal ajouté (printerId:', config.mainPrinterId, ')');
  } else {
    console.log('[buildHybridPayload] ⊘ Ticket principal filtré (paiement non autorisé)');
  }

  // Tickets par catégorie: déjà nettoyés par splitTicketByCategory
  // En mode hybride, on envoie TOUJOURS les tickets de catégorie, meme si c'est la meme imprimante
  // Car l'utilisateur veut recevoir les deux types de tickets (classique + par catégorie)
  for (const categoryTicket of splitResult.tickets) {
    console.log('[buildHybridPayload] Processing category:', categoryTicket.categoryName, '(', categoryTicket.categoryId, ')');

    const mapping = await getPrinterForCategory(categoryTicket.categoryId);
    console.log('[buildHybridPayload] Mapping found:', mapping);

    if (mapping) {
      // Vérifier le filtre de paiement pour cette catégorie
      const shouldPrint = shouldPrintForPaymentFilter(
        mapping.paymentFilter,
        options.paymentMethod
      );

      console.log('[buildHybridPayload] Category payment filter check:', {
        category: categoryTicket.categoryName,
        paymentFilter: mapping.paymentFilter,
        paymentMethod: options.paymentMethod,
        shouldPrint
      });

      if (shouldPrint) {
        // Toujours ajouter le ticket de catégorie, meme si c'est la meme imprimante que la principale
        // L'utilisateur veut les deux tickets: un complet (classique) et un par catégorie
        tickets.push({
          printerId: mapping.printerId,
          printerName: mapping.printerName,
          categoryId: categoryTicket.categoryId,
          categoryName: categoryTicket.categoryName,
          content: categoryTicket.content,
          isPrimary: false,
        });

        if (mapping.printerId === config.mainPrinterId) {
          console.log('[buildHybridPayload] ✓ Ticket catégorie ajouté (meme imprimante que principale):', categoryTicket.categoryName, '→', mapping.printerName);
        } else {
          console.log('[buildHybridPayload] ✓ Ticket catégorie ajouté (imprimante différente):', categoryTicket.categoryName, '→', mapping.printerName);
        }
      } else {
        console.log('[buildHybridPayload] ⊘ Ticket catégorie filtré (paiement non autorisé):', categoryTicket.categoryName);
      }
    } else {
      console.warn('[buildHybridPayload] ✗ No mapping found for category:', categoryTicket.categoryName);
    }
  }

  console.log('[buildHybridPayload] ========================================');
  console.log('[buildHybridPayload] FINAL PAYLOAD - Total tickets:', tickets.length);
  tickets.forEach((ticket, index) => {
    console.log(`[buildHybridPayload] Ticket ${index + 1}:`, {
      printerId: ticket.printerId,
      printerName: ticket.printerName,
      isPrimary: ticket.isPrimary,
      categoryId: ticket.categoryId,
      categoryName: ticket.categoryName,
      contentLength: ticket.content.length
    });
  });
  console.log('[buildHybridPayload] ========================================');

  return {
    orderNumber: options.orderNumber,
    orderDate: options.orderDate,
    orderType: options.orderType,
    tableNumber: options.tableNumber,
    customerName: options.customerName,
    paymentMethod: options.paymentMethod,
    mode: 'hybrid',
    tickets,
  };
};

export const buildRoutingPayload = async (
  options: BuildPayloadOptions,
  mode?: PrinterRoutingMode
): Promise<RoutedPrintPayload> => {
  const config = await getRoutingConfig();
  const effectiveMode = mode || config.mode;

  switch (effectiveMode) {
    case 'classic':
      return buildClassicPayload(options);
    case 'by-category':
      return buildByCategoryPayload(options);
    case 'hybrid':
      return buildHybridPayload(options);
    default:
      return buildClassicPayload(options);
  }
};

export const createTestPayload = async (
  printerId: string,
  printerName: string
): Promise<RoutedPrintPayload> => {
  const testContent = `
========================================
           TEST D'IMPRESSION
========================================

Imprimante: ${printerName}
ID: ${printerId}
Date: ${new Date().toLocaleString('fr-FR')}

========================================
           Test réussi !
========================================

Ceci est un ticket de test pour vérifier
que l'imprimante fonctionne correctement.

Si vous voyez ce message, l'impression
fonctionne parfaitement.

========================================


`;

  return {
    orderNumber: 'TEST',
    orderDate: new Date().toLocaleString('fr-FR'),
    orderType: 'takeaway',
    mode: 'classic',
    tickets: [
      {
        printerId,
        printerName,
        content: testContent,
        isPrimary: true,
      },
    ],
  };
};
